// ---------------------------------------------------------------------------
// Keyboard Shortcuts — tinykeys-based shortcut binding for the flow editor.
//
// Every handler ignores events that originate inside editable targets
// (INPUT/TEXTAREA/SELECT/contentEditable) so typing in parameter fields is
// never hijacked (F42 — same guard as page-editor keyboard shortcuts).
//
// Canvas-node shortcuts ($mod+a select-all-nodes, Delete/Backspace
// remove-selected-nodes, $mod+d duplicate-nodes) are intentionally absent:
// the list-based Behavior surface hydrates canvas nodes but never renders
// them, so those window-level bindings mutated invisible state — Cmd+A in a
// text input selected hidden nodes and a following Backspace deleted
// persisted steps with no feedback. (Step-buffer mutations themselves now
// route through the MCP bus — cogo.studio.editFlowSteps, F4 — so user step
// edits ARE undoable via the $mod+z binding below; the remaining hazard is
// canvas-node state that nothing renders.) Canvas-scoped shortcuts must bind
// to the canvas element if/when a canvas surface mounts, never to window.
// ---------------------------------------------------------------------------

import { useEffect } from 'react'
// @ts-expect-error — tinykeys exports not resolved under moduleResolution: bundler
import { tinykeys } from 'tinykeys'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { mcpBus } from '@/mcp/studio-mcp-bus'

/** Check if the keyboard event target is inside a text input */
function isEditingText(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement
  if (!target) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

export function useShortcuts() {
  useEffect(() => {
    const unsubscribe = tinykeys(window, {
      // Undo / Redo (via MCP undo middleware)
      '$mod+z': (e: KeyboardEvent) => {
        if (isEditingText(e)) return // Allow native undo in text inputs
        e.preventDefault()
        mcpBus.executeTrustedUi({ tool: 'cogo.studio.undo', params: {} })
      },
      '$mod+Shift+z': (e: KeyboardEvent) => {
        if (isEditingText(e)) return // Allow native redo in text inputs
        e.preventDefault()
        mcpBus.executeTrustedUi({ tool: 'cogo.studio.redo', params: {} })
      },

      Escape: (e: KeyboardEvent) => {
        if (isEditingText(e)) return
        useFlowEditorStore.getState().clearSelection()
      },
    })

    return unsubscribe
  }, [])
}
